import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingSpinner } from "@tngtech/tng-ui";
import { MouseEventHandler, PropsWithChildren } from "react";
import { useTranslator } from "../i18n/translator.ts";
import { ButtonWithConfirmation } from "./ButtonWithConfirmation.tsx";

interface FormContainerProps {
  title: string;
  onClose: MouseEventHandler;
  saveConfig: {
    onSave: MouseEventHandler;
    isSaveDisabled?: boolean;
    saveButtonText?: string;
  };
  deleteConfig?: {
    onDelete: () => void;
    confirmationMessage: string;
    isDeleteDisabled?: boolean;
  };
  isLoading?: boolean;
}

export const FormContainer = ({
  title,
  onClose,
  saveConfig,
  deleteConfig,
  isLoading = false,
  children,
}: PropsWithChildren<FormContainerProps>) => {
  const translate = useTranslator();

  const content = isLoading ? (
    <Stack direction="row" justifyContent="center">
      <LoadingSpinner />
    </Stack>
  ) : (
    <>
      {children}
      <Divider />
      <Stack direction={"row-reverse"} spacing={2}>
        {saveConfig && (
          <Button
            type="submit"
            variant="contained"
            onClick={saveConfig.onSave}
            disabled={saveConfig.isSaveDisabled ?? false}
          >
            {saveConfig.saveButtonText ?? translate("saveChanges")}
          </Button>
        )}
        <Button type="button" variant="outlined" onClick={onClose}>
          {translate("cancel")}
        </Button>
        {deleteConfig && (
          <ButtonWithConfirmation
            handleConfirm={deleteConfig.onDelete}
            dialogTitle={translate("deleteConfirmationTitle")}
            dialogMessage={deleteConfig.confirmationMessage}
          >
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={deleteConfig.isDeleteDisabled ?? false}
            >
              {translate("delete")}
            </Button>
          </ButtonWithConfirmation>
        )}
      </Stack>
    </>
  );

  return (
    <Box component={"form"} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant={"h4"}>{title}</Typography>
          <IconButton
            type="button"
            onClick={onClose}
            aria-label={translate("closeForm")}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        {content}
      </Stack>
    </Box>
  );
};
